import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContent } from "../../components/PageContent";
import { Card } from "../../components/Card";
import { LoadingContainer } from "../../components/LoadingContainer";
import { InfoBox } from "../../components/InfoBox";
import { EmptyState } from "../../components/EmptyState";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../../components/Table";
import { useGetStoreDebtsQuery } from "../../query/storeDebtsQuery";
import type { ChainId } from "@KassaroAB/shared/types/ids";
import { formatMoney } from "../../utils/formatMoney";
import { getDebtorName } from "./storeDebtDisplay";

export const StoreDebtsView = () => {
  const { t } = useTranslation();
  const { chainId } = useParams<{ chainId: ChainId }>();
  const { data: storeDebts, isLoading, error } = useGetStoreDebtsQuery(chainId);

  if (isLoading && !storeDebts) {
    return (
      <PageContent title={t("storeDebtsView.title")}>
        <LoadingContainer message={t("storeDebtsView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent title={t("storeDebtsView.title")}>
      {error && (
        <InfoBox variant="error">{t("storeDebtsView.errorLoad")}</InfoBox>
      )}
      {storeDebts && storeDebts.length > 0 ? (
        <Card>
          <Table minWidth="640px">
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("storeDebtsView.store1")}</TableHeaderCell>
                <TableHeaderCell>{t("storeDebtsView.store2")}</TableHeaderCell>
                <TableHeaderCell>{t("storeDebtsView.debtor")}</TableHeaderCell>
                <TableHeaderCell>{t("storeDebtsView.amount")}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {storeDebts.map((storeDebt) => (
                <TableRow
                  key={storeDebt.id}
                  to={`/chains/${chainId}/storeDebts/${storeDebt.id}`}
                >
                  <TableCell noWrap>
                    {storeDebt.storeName1} ({storeDebt.orgNumber1})
                  </TableCell>
                  <TableCell noWrap>
                    {storeDebt.storeName2} ({storeDebt.orgNumber2})
                  </TableCell>
                  <TableCell noWrap>
                    {getDebtorName(storeDebt, t("storeDebtsView.settled"))}
                  </TableCell>
                  <TableCell noWrap>
                    {formatMoney({
                      amount: Math.abs(storeDebt.balanceForStore1.amount),
                      currency: storeDebt.balanceForStore1.currency,
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : !error ? (
        <EmptyState message={t("storeDebtsView.empty")} />
      ) : null}
    </PageContent>
  );
};
